import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../auth/useAuthStore';
import { SummaryTable } from './AnalysisSummaryTable';
import { usePortalAccess } from './PortalAccessContext';
import { PortalBreadcrumb } from './PortalBreadcrumb';
import { PortalTopbar } from './PortalTopbar';
import {
  clientPortalApi,
  DAY_TYPES,
  MUNICIPAL_BANDS,
  type DayType,
  type LineSummaryRow,
  type SystemTotals,
} from './api';
import './dashboard.css';
import { formatFleet, formatKm, formatPct, formatPopulation } from './summaryFormat';

type LoadState = 'loading' | 'error' | 'ready';

const MONTH_NAMES_ES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];

function SystemStat({
  label,
  value,
  note,
}: {
  label: string;
  value: string;
  note?: string;
}): JSX.Element {
  return (
    <div className="db-system-stat">
      <p className="db-system-stat-value">{value}</p>
      <p className="db-system-stat-label">{label}</p>
      {note && <p className="db-system-stat-note">{note}</p>}
    </div>
  );
}

function SystemLevel({ title, children }: { title: string; children: ReactNode }): JSX.Element {
  return (
    <div className="db-system-level">
      <p className="db-system-level-title">{title}</p>
      <div className="db-system-totals-grid">{children}</div>
    </div>
  );
}

function SystemDimensionamiento({ totals }: { totals: SystemTotals }): JSX.Element {
  const bandaLabel = totals.bandaCritica
    ? `banda crítica: ${totals.bandaCritica.start}–${totals.bandaCritica.end}`
    : 'sin datos';
  const reserva = totals.flotaConReserva;
  return (
    <SystemLevel title="Nivel 1 · Dimensionamiento">
      <SystemStat
        label="Flota operativa máxima"
        value={formatFleet(totals.assignedFleetRequired)}
        note="flota física asignada — suma del máximo diario de cada una de las 21 rutas"
      />
      <SystemStat
        label="Flota de referencia +10%"
        value={formatFleet(reserva.pct10)}
        note={`+5%: ${formatFleet(reserva.pct5)} · +15%: ${formatFleet(reserva.pct15)} (escenarios, no valor oficial)`}
      />
      <SystemStat
        label="Máximo simultáneo en servicio"
        value={formatFleet(totals.maxConcurrentVehicles)}
        note={`${bandaLabel} — no es la flota asignada`}
      />
      <SystemStat
        label="Flota — banda seleccionada"
        value={formatFleet(totals.fleetRequiredSystemBand)}
        note={`${totals.fleetRequiredSystemBandLineCount} líneas`}
      />
    </SystemLevel>
  );
}

function SystemProduccion({ totals }: { totals: SystemTotals }): JSX.Element {
  const mesLabel = `${MONTH_NAMES_ES[totals.mesCalculado.month - 1]} ${totals.mesCalculado.year}`;
  return (
    <SystemLevel title="Nivel 2 · Producción">
      <SystemStat
        label="Km operativos Hábil"
        value={formatKm(totals.kmOperativosPorDia['Hábil'])}
      />
      <SystemStat
        label="Km operativos Sábado"
        value={formatKm(totals.kmOperativosPorDia['Sábado'])}
      />
      <SystemStat
        label="Km operativos Domingo"
        value={formatKm(totals.kmOperativosPorDia['Domingo'])}
      />
      <SystemStat
        label="Km mensuales"
        value={formatPopulation(Math.round(totals.kmMensualesTotales))}
        note={mesLabel}
      />
      <SystemStat
        label="Km anuales proyectados"
        value={formatPopulation(Math.round(totals.kmAnualesProyectados))}
        note={`calendario ${totals.anioCalculado}`}
      />
      <SystemStat
        label="Servicios / día"
        value={String(totals.serviciosTotalesDia)}
        note={`${totals.produccionDiaLineCount} líneas`}
      />
      <SystemStat
        label="Horas operativas / día"
        value={`${totals.horasOperativasTotalesDia.toFixed(1)} h`}
      />
    </SystemLevel>
  );
}

function SystemCobertura({ totals }: { totals: SystemTotals }): JSX.Element {
  return (
    <SystemLevel title="Nivel 3 · Cobertura">
      <SystemStat
        label="Población cubierta 2 min"
        value={totals.coverage2min ? formatPct(totals.coverage2min.populationCoveragePct) : '—'}
        note={
          totals.coverage2min
            ? `${formatPopulation(totals.coverage2min.populationCovered)} hab. (unión)`
            : 'calculando…'
        }
      />
      <SystemStat
        label="Población cubierta 5 min"
        value={totals.coverage5min ? formatPct(totals.coverage5min.populationCoveragePct) : '—'}
        note={
          totals.coverage5min
            ? `${formatPopulation(totals.coverage5min.populationCovered)} hab. (unión)`
            : 'calculando…'
        }
      />
      <SystemStat
        label="Paradas físicas"
        value={totals.physicalStopsCount !== null ? formatPopulation(totals.physicalStopsCount) : '—'}
        note="universo TransitStop"
      />
    </SystemLevel>
  );
}

function SystemTotalsBlock({ totals }: { totals: SystemTotals }): JSX.Element {
  return (
    <div className="db-system-totals">
      <p className="db-system-totals-title">Total sistema</p>
      <div className="db-system-columns">
        <SystemDimensionamiento totals={totals} />
        <SystemProduccion totals={totals} />
        <SystemCobertura totals={totals} />
      </div>
      <p className="db-system-fingerprint" title={totals.scenarioFingerprint}>
        escenario {totals.scenarioFingerprint.slice(0, 8)}
      </p>
    </div>
  );
}

function SummaryFilters({
  dayType,
  onDayTypeChange,
  bandIndex,
  onBandIndexChange,
}: {
  dayType: DayType;
  onDayTypeChange: (value: DayType) => void;
  bandIndex: number;
  onBandIndexChange: (value: number) => void;
}): JSX.Element {
  return (
    <div className="db-summary-filters">
      <label>
        <span>Día tipo</span>
        <select value={dayType} onChange={e => onDayTypeChange(e.target.value as DayType)}>
          {DAY_TYPES.map(dt => (
            <option key={dt} value={dt}>
              {dt}
            </option>
          ))}
        </select>
      </label>
      <label>
        <span>Banda horaria</span>
        <select value={bandIndex} onChange={e => onBandIndexChange(Number(e.target.value))}>
          {MUNICIPAL_BANDS.map((b, i) => (
            <option key={`${b.start}-${b.end}`} value={i}>
              {b.start}–{b.end}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

function useAnalysisSummary(
  slug: string,
  token: string | null,
  dayType: DayType,
  bandIndex: number
): {
  rows: LineSummaryRow[];
  systemTotals: SystemTotals | null;
  loadState: LoadState;
  errorMessage: string | null;
} {
  const [rows, setRows] = useState<LineSummaryRow[]>([]);
  const [systemTotals, setSystemTotals] = useState<SystemTotals | null>(null);
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const band = MUNICIPAL_BANDS[bandIndex];

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoadState('loading');
    clientPortalApi
      .getAnalysisSummary(slug, token, dayType, band)
      .then(result => {
        if (cancelled) return;
        setRows(result.lines);
        setSystemTotals(result.systemTotals);
        setLoadState('ready');
      })
      .catch(() => {
        if (cancelled) return;
        setErrorMessage('No se pudo cargar la tabla resumen. Reintentá en unos segundos.');
        setLoadState('error');
      });
    return (): void => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `band` se deriva de bandIndex, no es una dependencia independiente
  }, [slug, token, dayType, bandIndex]);

  return { rows, systemTotals, loadState, errorMessage };
}

type ExportFormat = 'xlsx' | 'csv' | 'json';
type ExportStatus = 'idle' | 'loading' | 'done' | 'error';

const EXPORT_FORMAT_LABELS: Record<ExportFormat, string> = {
  xlsx: 'Excel (.xlsx)',
  csv: 'CSV (.zip)',
  json: 'JSON',
};

/**
 * "Exportar datos" — descarga el escenario configurado (sistema + 17
 * líneas + 1735 paradas) tal cual lo usa el motor, en el formato elegido.
 * Nunca navega a otra pantalla: dispara la descarga del navegador
 * directamente vía `clientPortalApi.downloadAnalysisExport`.
 */
function ExportMenu({ slug, token }: { slug: string; token: string | null }): JSX.Element {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<ExportStatus>('idle');

  async function handleExport(format: ExportFormat): Promise<void> {
    if (!token) return;
    setOpen(false);
    setStatus('loading');
    try {
      await clientPortalApi.downloadAnalysisExport(slug, token, format);
      setStatus('done');
    } catch {
      setStatus('error');
    } finally {
      setTimeout(() => setStatus('idle'), 3000);
    }
  }

  return (
    <div className="db-export-menu">
      <button
        type="button"
        className="db-system-map-link"
        onClick={() => setOpen(v => !v)}
        disabled={!token}
      >
        Exportar datos ↓
      </button>
      {open && (
        <div className="db-export-dropdown">
          {(Object.keys(EXPORT_FORMAT_LABELS) as ExportFormat[]).map(format => (
            <button key={format} type="button" onClick={() => void handleExport(format)}>
              {EXPORT_FORMAT_LABELS[format]}
            </button>
          ))}
        </div>
      )}
      {status === 'loading' && <span className="db-export-status">Preparando exportación…</span>}
      {status === 'done' && (
        <span className="db-export-status db-export-status-ok">Exportación lista</span>
      )}
      {status === 'error' && (
        <span className="db-export-status db-export-status-error">No se pudo exportar. Reintentá.</span>
      )}
    </div>
  );
}

/**
 * Landing de "Análisis técnico de la red" (`/analysis`). Tabla resumen
 * comparativa de todas las líneas del catálogo (`GET
 * /portals/:slug/analysis-summary`), con Día tipo y Banda horaria como
 * únicos filtros de contexto arriba de la tabla — nunca 20 columnas de
 * filtros. Reemplaza el grid de cards anterior: la pantalla inicial ahora
 * es comparativa, no una galería.
 */
export function AnalysisHubPage(): JSX.Element {
  const { slug } = usePortalAccess();
  const { token } = useAuthStore();
  const navigate = useNavigate();

  const [dayType, setDayType] = useState<DayType>('Hábil');
  const [bandIndex, setBandIndex] = useState(0);
  const { rows, systemTotals, loadState, errorMessage } = useAnalysisSummary(
    slug,
    token,
    dayType,
    bandIndex
  );

  return (
    <div className="db-page">
      <PortalTopbar />

      <div className="db-wrap db-wrap-wide">
        <Link to="/" className="db-portal-back-link">
          ← Bahía Blanca
        </Link>
        <PortalBreadcrumb segments={[{ label: 'Inicio', to: '/' }, { label: 'Sistema de Transporte' }]} />

        <div className="db-section-header" style={{ paddingTop: '0.75rem' }}>
          <div>
            <div className="db-section-label" style={{ padding: 0 }}>
              Sistema de Transporte
            </div>
            <p className="db-section-sublabel">Dashboard del sistema · Análisis técnico de la red</p>
          </div>
          <div className="db-header-actions">
            <ExportMenu slug={slug} token={token} />
            <Link to="/analysis/system-map" className="db-system-map-link">
              Mapa del sistema →
            </Link>
          </div>
        </div>

        <SummaryFilters
          dayType={dayType}
          onDayTypeChange={setDayType}
          bandIndex={bandIndex}
          onBandIndexChange={setBandIndex}
        />

        {loadState === 'error' && <p className="db-summary-error">{errorMessage}</p>}
        {loadState !== 'error' && systemTotals && <SystemTotalsBlock totals={systemTotals} />}
        {loadState !== 'error' && (
          <SummaryTable
            rows={rows}
            loadState={loadState}
            dayType={dayType}
            bandIndex={bandIndex}
            onOpen={id => navigate(`/analysis/${id}`)}
          />
        )}
      </div>
    </div>
  );
}
